/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/SectionContent/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger } from "../../utils/logger.js";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import styles from "./SectionContent.module.css";
import CorporateIdentity from "../Sections/CorporateIdentity";
import Description from "../Sections/Description";
import ContactInformation from "../Sections/ContactInformation";
import ContactsData from "../Sections/ContactsData";
import Location from "../Sections/Location";
import CommercialOffer from "../Sections/CommercialOffer";
import Skills from "../Sections/Skills";
import SocialMedia from "../Sections/SocialMedia";
import Documents from "../Sections/Documents";
import AreasOfInterest from "../Sections/AreasOfInterest";
import Certifications from "../Sections/Certifications";

const SectionContent = ({
  activeSection,
  company,
  onFieldChange,
  onValidationChange,
  selectedProfile = 0,
}) => {
  const [sectionErrors, setSectionErrors] = useState({});

  const handleFieldChange = (section, field, value) => {
    if (onFieldChange) {
      onFieldChange(section, field, value);
    }
  };

  const handleValidationChange = useCallback((sectionName, hasErrors) => {
    setSectionErrors((prev) => ({
      ...prev,
      [sectionName]: hasErrors,
    }));
  }, []);

  // Memoize the validation state to prevent unnecessary re-renders
  const hasAnyErrors = useMemo(() => {
    return Object.values(sectionErrors).some((hasErrors) => hasErrors);
  }, [sectionErrors]);

  // Notify parent of validation changes only when the overall state changes
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(hasAnyErrors);
    }
  }, [hasAnyErrors, onValidationChange]);

  const renderContent = () => {
    switch (activeSection) {
      case "informacion-general":
        return (
          <div className={styles.sectionsContainer}>
            <CorporateIdentity
              company={company}
              onFieldChange={(field, value) =>
                handleFieldChange("informacionGeneral", field, value)
              }
              onValidationChange={(hasErrors) =>
                handleValidationChange("corporateIdentity", hasErrors)
              }
              selectedProfile={selectedProfile}
              key={`corporate-identity-${company?._lastUpdate || ""}-${selectedProfile}`}
            />
            <Description
              company={company}
              onFieldChange={(field, value) =>
                handleFieldChange("informacionGeneral", field, value)
              }
              onValidationChange={(hasErrors) =>
                handleValidationChange("description", hasErrors)
              }
              selectedProfile={selectedProfile}
              key={`description-${company?._lastUpdate || ""}-${selectedProfile}`}
            />
            <ContactInformation
              company={company}
              onFieldChange={(field, value) =>
                handleFieldChange("informacionGeneral", field, value)
              }
              onValidationChange={(hasErrors) =>
                handleValidationChange("contactInformation", hasErrors)
              }
              selectedProfile={selectedProfile}
              key={`contact-info-${company?._lastUpdate || ""}-${selectedProfile}`}
            />
          </div>
        );
      case "contacto-ubicacion":
        return (
          <div className={styles.sectionsContainer}>
            <ContactsData
              company={company}
              onFieldChange={(field, value) =>
                handleFieldChange("contactoUbicacion", field, value)
              }
              onValidationChange={(hasErrors) =>
                handleValidationChange("contactsData", hasErrors)
              }
              selectedProfile={selectedProfile}
              key={`contacts-data-${company?._lastUpdate || ""}-${selectedProfile}`}
            />
            <Location
              company={company}
              onFieldChange={(field, value) =>
                handleFieldChange("contactoUbicacion", field, value)
              }
              onValidationChange={(hasErrors) =>
                handleValidationChange("location", hasErrors)
              }
              selectedProfile={selectedProfile}
              key={`location-${company?._lastUpdate || ""}-${selectedProfile}`}
            />
          </div>
        );
      case "productos-servicios":
        return (
          <div className={styles.sectionsContainer}>
            <CommercialOffer
              company={company}
              onFieldChange={(field, value) =>
                handleFieldChange("productosServicios", field, value)
              }
              onValidationChange={(hasErrors) =>
                handleValidationChange("commercialOffer", hasErrors)
              }
              selectedProfile={selectedProfile}
              key={`commercial-offer-${company?._lastUpdate || ""}-${selectedProfile}`}
            />
            <Skills
              company={company}
              onFieldChange={(field, value) =>
                handleFieldChange("productosServicios", field, value)
              }
              onValidationChange={(hasErrors) =>
                handleValidationChange("skills", hasErrors)
              }
              selectedProfile={selectedProfile}
              key={`skills-${company?._lastUpdate || ""}-${selectedProfile}`}
            />
          </div>
        );
      case "redes-presencia":
        return (
          <div className={styles.sectionsContainer}>
            <SocialMedia
              company={company}
              onFieldChange={(field, value) =>
                handleFieldChange("redesPresencia", field, value)
              }
              onValidationChange={(hasErrors) =>
                handleValidationChange("socialMedia", hasErrors)
              }
              selectedProfile={selectedProfile}
              key={`social-media-${company?._lastUpdate || ""}-${selectedProfile}`}
            />
          </div>
        );
      case "vinculacion-networking":
        return (
          <div className={styles.sectionsContainer}>
            <Documents
              company={company}
              onFieldChange={(field, value) =>
                handleFieldChange("vinculacionNetworking", field, value)
              }
              onValidationChange={(hasErrors) =>
                handleValidationChange("documents", hasErrors)
              }
              selectedProfile={selectedProfile}
              key={`documents-${company?._lastUpdate || ""}-${selectedProfile}`}
            />
            <AreasOfInterest
              company={company}
              onFieldChange={(field, value) =>
                handleFieldChange("vinculacionNetworking", field, value)
              }
              onValidationChange={(hasErrors) =>
                handleValidationChange("areasOfInterest", hasErrors)
              }
              selectedProfile={selectedProfile}
              key={`areas-of-interest-${company?._lastUpdate || ""}-${selectedProfile}`}
            />
            <Certifications
              company={company}
              onFieldChange={(field, value) =>
                handleFieldChange("vinculacionNetworking", field, value)
              }
              onValidationChange={(hasErrors) =>
                handleValidationChange("certifications", hasErrors)
              }
              selectedProfile={selectedProfile}
              key={`certifications-${company?._lastUpdate || ""}-${selectedProfile}`}
            />
          </div>
        );
      default:
        logger.info("No section matched:", { data: activeSection });
        return null;
    }
  };

  return (
    <div className={styles.sectionContentContainer}>{renderContent()}</div>
  );
};

export default SectionContent;
